import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Upload, FileText, CheckCircle2, Clock, XCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function KYCApplicationPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    aadhaar_number: '',
    pan_number: '',
    farm_address: '',
    farm_size: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_account_holder: '',
    bank_name: '',
    upi_id: '',
  });
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const { data } = await api.get('/kyc/status');
        setKycStatus(data);
      } catch {} finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [api]);

  // Auto-poll KYC status every 10 seconds while pending
  useEffect(() => {
    if (kycStatus?.status !== 'pending') return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get('/kyc/status');
        setKycStatus(data);
        if (data.status === 'approved') {
          refreshUser();
        }
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [kycStatus?.status, api, refreshUser]);

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setDocuments(prev => [...prev, { name: file.name, path: data.storage_path }]);
      }
      toast.success('Document uploaded!');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.aadhaar_number || !form.pan_number || !form.farm_address) {
      toast.error('Fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/kyc/apply', {
        ...form,
        document_paths: documents.map(d => d.path),
      });
      toast.success('Application submitted! We\'ll review it shortly.');
      const { data } = await api.get('/kyc/status');
      setKycStatus(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppShell showNav={false}>
        <div className="p-4 flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0B4F3F] border-t-transparent" />
        </div>
      </AppShell>
    );
  }

  // Already a farmer
  if (user?.role === 'farmer') {
    return (
      <AppShell showNav={false}>
        <div className="p-4 text-center pt-20" data-testid="kyc-already-farmer">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#15803D] mb-4" />
          <h2 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">You're Verified!</h2>
          <p className="text-sm text-[#5B6675] mt-2">You're already a verified farmer.</p>
          <Button
            onClick={() => navigate('/farmer-dashboard')}
            className="mt-6 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full px-8"
          >
            Go to Dashboard
          </Button>
        </div>
      </AppShell>
    );
  }

  // Status display
  if (kycStatus?.status === 'pending') {
    return (
      <AppShell showNav={false}>
        <div className="p-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[#5B6675] mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-center pt-12" data-testid="kyc-pending">
            <Clock className="w-16 h-16 mx-auto text-[#0B4F3F] mb-4 animate-pulse" />
            <h2 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">Under Review</h2>
            <p className="text-sm text-[#5B6675] mt-2 max-w-xs mx-auto">
              Your farmer application is being reviewed. This usually takes about a minute.
            </p>
            <p className="text-xs text-[#0B4F3F] mt-4 font-medium">Checking status automatically...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (kycStatus?.status === 'rejected') {
    return (
      <AppShell showNav={false}>
        <div className="p-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[#5B6675] mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-center pt-12" data-testid="kyc-rejected">
            <XCircle className="w-16 h-16 mx-auto text-[#B91C1C] mb-4" />
            <h2 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">Application Rejected</h2>
            <p className="text-sm text-[#5B6675] mt-2 max-w-xs mx-auto">
              {kycStatus.application?.admin_notes || 'Your application was rejected. Please contact support.'}
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (kycStatus?.status === 'approved') {
    refreshUser();
    return (
      <AppShell showNav={false}>
        <div className="p-4 text-center pt-20" data-testid="kyc-approved">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#15803D] mb-4" />
          <h2 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">Approved!</h2>
          <p className="text-sm text-[#5B6675] mt-2">Your farmer verification is complete.</p>
          <Button
            onClick={() => { refreshUser(); navigate('/farmer-dashboard'); }}
            className="mt-6 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full px-8"
          >
            Go to Dashboard
          </Button>
        </div>
      </AppShell>
    );
  }

  // Application Form
  return (
    <AppShell showNav={false}>
      <div className="p-4" data-testid="kyc-application-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-[#EFF2F7] flex items-center justify-center">
            <ArrowLeft className="w-4 h-4 text-[#0B1620]" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">Become a Farmer</h1>
            <p className="text-xs text-[#5B6675]">Verify your identity to start selling on Frootex</p>
          </div>
        </div>

        {/* Trust banner */}
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-[#0B4F3F]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#0B1620]">Secure Verification</p>
            <p className="text-xs text-[#5B6675]">Your documents are encrypted and handled securely</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Aadhaar Number *</Label>
            <Input
              data-testid="aadhaar-input"
              value={form.aadhaar_number}
              onChange={(e) => setForm(prev => ({ ...prev, aadhaar_number: e.target.value }))}
              placeholder="XXXX-XXXX-XXXX"
              className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">PAN Number *</Label>
            <Input
              data-testid="pan-input"
              value={form.pan_number}
              onChange={(e) => setForm(prev => ({ ...prev, pan_number: e.target.value }))}
              placeholder="ABCDE1234F"
              className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Farm Address *</Label>
            <Input
              data-testid="farm-address-input"
              value={form.farm_address}
              onChange={(e) => setForm(prev => ({ ...prev, farm_address: e.target.value }))}
              placeholder="Village, District, State"
              className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Farm Size</Label>
            <Input
              data-testid="farm-size-input"
              value={form.farm_size}
              onChange={(e) => setForm(prev => ({ ...prev, farm_size: e.target.value }))}
              placeholder="e.g., 5 acres"
              className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
            />
          </div>

          {/* Document Upload */}
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Upload Documents</Label>
            <p className="text-xs text-[#5B6675] mb-3">Aadhaar card, PAN card, land ownership proof</p>
            
            {documents.map((doc, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#EFF2F7] rounded-lg p-2 mb-2">
                <FileText className="w-4 h-4 text-[#0B4F3F]" />
                <span className="text-xs text-[#0B1620] truncate flex-1">{doc.name}</span>
              </div>
            ))}

            <label
              data-testid="upload-doc-btn"
              className="flex items-center justify-center gap-2 w-full h-11 border-2 border-dashed border-[#E4E8EF] rounded-xl cursor-pointer hover:border-[#0B4F3F] transition-colors"
            >
              {uploading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0B4F3F] border-t-transparent" />
              ) : (
                <>
                  <Upload className="w-4 h-4 text-[#5B6675]" />
                  <span className="text-sm text-[#5B6675]">Choose files</span>
                </>
              )}
              <input type="file" accept="image/*,.pdf" multiple onChange={handleDocUpload} className="hidden" />
            </label>
          </div>

          {/* Bank Details (Optional) */}
          <div className="pt-2 border-t border-[#E4E8EF]">
            <p className="text-sm font-medium text-[#0B1620] mb-1">Bank Details (optional)</p>
            <p className="text-xs text-[#5B6675] mb-4">Add your bank details for receiving payments</p>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#5B6675] mb-1 block">Account Holder Name</Label>
                <Input
                  data-testid="bank-holder-input"
                  value={form.bank_account_holder}
                  onChange={(e) => setForm(prev => ({ ...prev, bank_account_holder: e.target.value }))}
                  placeholder="Name as per bank"
                  className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#5B6675] mb-1 block">Account Number</Label>
                  <Input
                    data-testid="bank-account-input"
                    value={form.bank_account_number}
                    onChange={(e) => setForm(prev => ({ ...prev, bank_account_number: e.target.value }))}
                    placeholder="Account number"
                    className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#5B6675] mb-1 block">IFSC Code</Label>
                  <Input
                    data-testid="bank-ifsc-input"
                    value={form.bank_ifsc}
                    onChange={(e) => setForm(prev => ({ ...prev, bank_ifsc: e.target.value }))}
                    placeholder="SBIN0001234"
                    className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#5B6675] mb-1 block">Bank Name</Label>
                <Input
                  data-testid="bank-name-input"
                  value={form.bank_name}
                  onChange={(e) => setForm(prev => ({ ...prev, bank_name: e.target.value }))}
                  placeholder="e.g., State Bank of India"
                  className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                />
              </div>
              <div>
                <Label className="text-xs text-[#5B6675] mb-1 block">UPI ID (optional)</Label>
                <Input
                  data-testid="upi-id-input"
                  value={form.upi_id}
                  onChange={(e) => setForm(prev => ({ ...prev, upi_id: e.target.value }))}
                  placeholder="e.g., name@upi"
                  className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                />
              </div>
            </div>
          </div>

          <Button
            data-testid="submit-kyc-btn"
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-base transition-colors"
          >
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </form>
      </div>
    </AppShell>
  );
}
